import React, { useState, useEffect } from 'react';
import './Articles.css';
import { db } from '../../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import YouTubeSection, { YouTubeEmbed, getYouTubeId } from '../YouTubeSection';

function Articles() {
    const [articles, setArticles] = useState([]);
    const [selectedArticle, setSelectedArticle] = useState(null);

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                const q = query(collection(db, "articles"), orderBy("createdAt", "desc"));
                const snap = await getDocs(q);
                const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setArticles(data);
            } catch (err) {
                console.error("Eroare la citirea articolelor:", err);
            }
        };
        fetchArticles();
    }, []);

    // --- HELPER 1: Găsește prima imagine/video relevantă pentru Card Preview ---
    const getMainImage = (article) => {
        // A. Logica Nouă: "Blocks" (Blog Style)
        if (article.blocks && article.blocks.length > 0) {
            // Căutăm primul bloc de tip imagine
            const imgBlock = article.blocks.find(b => b.type === 'image' && b.url);
            if (imgBlock) return imgBlock.url;

            // Dacă nu, primul clip YouTube (thumbnail)
            const ytBlock = article.blocks.find(b => b.type === 'youtube' && getYouTubeId(b.url));
            if (ytBlock) return `https://img.youtube.com/vi/${getYouTubeId(ytBlock.url)}/hqdefault.jpg`;

            // Dacă nu, primul video
            const vidBlock = article.blocks.find(b => b.type === 'video' && b.url);
            if (vidBlock) {
                if (vidBlock.url.includes('youtube')) {
                    const ytId = vidBlock.url.split('v=')[1]?.split('&')[0] || vidBlock.url.split('/').pop();
                    return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
                }
                return "https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1000"; // Generic Video Icon
            }
        }

        // B. Logica Veche (Legacy)
        if (article.images && article.images.length > 0) return article.images[0];
        if (article.image) return article.image;

        // Verificăm video vechi
        const vidUrl = getVideoUrl(article);
        if (vidUrl && (vidUrl.includes('youtube') || vidUrl.includes('youtu.be'))) {
            const ytId = vidUrl.split('v=')[1]?.split('&')[0] || vidUrl.split('/').pop();
            return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
        }
        if (vidUrl) {
            return "https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1000";
        }

        // Fallback total
        return "https://via.placeholder.com/400x200?text=News";
    };

    // --- HELPER 2: Găsește un link video (Legacy support) ---
    const getVideoUrl = (article) => {
        if (article.videos && article.videos.length > 0) return article.videos[0];
        if (article.video) return article.video;
        if (article.videoUrl) return article.videoUrl;
        return null;
    };

    // --- HELPER 3: Randare Video Player ---
    const renderPlayer = (url, index) => {
        if (!url) return null;
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            const ytId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
            return (
                <div key={index} className="video-container">
                    <iframe
                        src={`https://www.youtube.com/embed/${ytId}`}
                        title="YouTube video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
            );
        }
        return (
            <div key={index} className="video-container native-video">
                <video controls width="100%" preload="metadata">
                    <source src={url} type="video/mp4" />
                    Browser-ul tău nu suportă redarea video.
                </video>
            </div>
        );
    };

    // --- MODAL CONTROLS ---
    const openArticle = (article) => {
        setSelectedArticle(article);
        document.body.style.overflow = 'hidden';
    };

    const closeArticle = () => {
        setSelectedArticle(null);
        document.body.style.overflow = 'auto';
    };

    // --- RENDERIZARE CONTINUT ARTICOL (VECHI SAU NOU) ---
    const renderArticleContent = (art) => {
        // 1. Cazul NOU: Are Blocks (Blog Style)
        if (art.blocks && art.blocks.length > 0) {
            return (
                <div className="blog-content">
                    {art.blocks.map((block, i) => {
                        if (block.type === 'text') {
                            return <p key={i} className="blog-text">{block.content}</p>;
                        }
                        if (block.type === 'image') {
                            return <img key={i} src={block.url} alt="blog-img" className="blog-img" />;
                        }
                        if (block.type === 'video') {
                            return renderPlayer(block.url, i);
                        }
                        if (block.type === 'youtube') {
                            return <YouTubeEmbed key={i} url={block.url} />;
                        }
                        return null;
                    })}
                </div>
            );
        }

        // 2. Cazul VECHI: Legacy (Gallery + Text)
        return (
            <>
                <div className="modal-gallery">
                    {(() => {
                        const vid = getVideoUrl(art);
                        if (vid) return renderPlayer(vid, 'main-video');
                    })()}
                    {art.videos && art.videos.length > 1 && art.videos.slice(1).map((v, i) => renderPlayer(v, i))}
                    {art.images && art.images.map((img, index) => (
                        <img key={index} src={img} alt="Detail" className="gallery-img"/>
                    ))}
                    {!art.images && art.image && (
                        <img src={art.image} alt="Detail" className="gallery-img"/>
                    )}
                </div>
                <div className="modal-body">
                    {art.content ? art.content.split('\n').map((para, i) => (
                        <p key={i}>{para}</p>
                    )) : <p>...</p>}
                </div>
            </>
        );
    };

    const getPreviewText = (art) => {
        if (art.content) return art.content.substring(0, 100);
        if (art.blocks) {
            const textBlock = art.blocks.find(b => b.type === 'text');
            return textBlock ? textBlock.content.substring(0, 100) : "Vezi imagini/video...";
        }
        return "Vezi detalii...";
    };

    return (
        <div className="articles-page">
            <h1 className="page-title">Latest <span className="highlight">News</span></h1>

            <div className="articles-grid">
                {articles.map(article => {
                    const coverImage = getMainImage(article);
                    // Verificăm dacă are vreun fel de video (inclusiv YouTube)
                    const hasVideo = getVideoUrl(article) || (article.blocks && article.blocks.some(b => b.type === 'video' || b.type === 'youtube'));
                    const isNewStyle = article.blocks && article.blocks.length > 0;

                    return (
                        <div key={article.id} className="article-card" onClick={() => openArticle(article)}>
                            <div
                                className="article-card-image"
                                style={{backgroundImage: `url(${coverImage})`}}
                            >
                                {hasVideo && (
                                    <div className="video-indicator">
                                        ▶ {isNewStyle ? "Watch" : "Video"}
                                    </div>
                                )}
                                <div className="overlay-date">
                                    {article.createdAt?.toDate ? article.createdAt.toDate().toLocaleDateString() : "Recent"}
                                </div>
                            </div>
                            <div className="article-card-content">
                                <h3>{article.title}</h3>
                                <p className="article-preview">
                                    {getPreviewText(article)}...
                                </p>
                                <span className="read-more">READ MORE &rarr;</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* --- VIDEOCLIPURI YOUTUBE (adăugate din Admin → YouTube, pagina "Articole") --- */}
            <div style={{ marginTop: '50px' }}>
                <YouTubeSection page="articles" heading="Videoclipuri" />
            </div>

            {/* --- MODAL --- */}
            {selectedArticle && (
                <div className="article-modal-backdrop" onClick={closeArticle}>
                    <div className="article-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="close-modal" onClick={closeArticle}>&times;</button>

                        <div className="modal-header">
                            <h2>{selectedArticle.title}</h2>
                            <small>
                                {selectedArticle.createdAt?.toDate ? selectedArticle.createdAt.toDate().toLocaleDateString() : ""}
                            </small>
                        </div>


                        {renderArticleContent(selectedArticle)}

                    </div>
                </div>
            )}
        </div>
    );
}

export default Articles;